import axios from 'axios';
import { createDocEntryStore } from './docStore';
import { materialsFor, type HardwareReading } from './materialsRun';
import { renderPartCrops, shrinkDataUrl } from './partCrop';
import { getParts } from './partsRun';
import type { Rgb } from './sampleColour';
import {
  getStandardMaterials, setStandardMaterials, type MaterialTexture, type StandardSurface,
} from './standardMaterials';

// THE HARDWARE'S HALF OF A MATERIALS READING — the default materials: a dowel's grain, a pad's
// felt, a screw's plating, and which of them each piece of hardware is. The product's own
// materials are measured off crops of its photographs, as four numbers the map is drawn from;
// these are inside the joint in every picture, so the same four numbers are asked of a model
// instead, described the way the measurement defines them and with the product's measured
// materials beside them for scale (see default_material_textures). Each piece of hardware is
// shown by its drawing in the manual's parts list.
//
// THE ANSWER IS A READING'S, not the manual's: it is kept with the components' half of the same
// press (see keepHardware), and written onto the Default materials and the parts when that
// reading is applied (applyHardware).

// Only whether it is out: what it said is on the reading.
type Snapshot = { busy: boolean };

const store = createDocEntryStore<Snapshot>(() => ({ snapshot: { busy: false } }));

export const useHardwareRun = store.useSnapshot;

// A PIECE OF HARDWARE THE CALL PUTS ON A MATERIAL: its index in the parts list and its name.
export type HardwarePart = { at: number; label: string };

// EACH ONE'S DRAWING, as the manual's parts list prints it — the crop the parts table shows,
// shrunk to what a line drawing needs. Null where there is no box to cut.
const drawings = async (docId: string, file: Blob, hardware: HardwarePart[]) => {
  const items = getParts(docId);
  const crops = await renderPartCrops(file, hardware.map((h) => items[h.at]));
  return Promise.all(crops.map((c) => (c ? shrinkDataUrl(c, 512) : null)));
};

// What the call is sent: the manual's list, the product's own measured materials for scale, and
// the hardware with its drawings.
const body = (docId: string, submodel: string, hardware: HardwarePart[], images: (string | null)[]) => ({
  materials: getStandardMaterials(docId).map((m) => ({ material: m.pair.material, color: m.pair.color, rgb: m.rgb })),
  measured: materialsFor(docId, submodel).materials.filter((m) => m.texture).map((m) => ({ name: m.name, texture: m.texture })),
  parts: hardware.map((h, i) => ({ name: h.label, image: images[i] ?? '' })),
});

export async function hardwarePrompt(
  docId: string, submodel: string, model: string, file: Blob, hardware: HardwarePart[],
) {
  const images = await drawings(docId, file, hardware);
  // THE PREVIEW IS SENT NO PICTURES — only which parts have one, so it numbers them alike.
  const { data } = await axios.post<{ prompt: string; model: string }>(
    '/api/notice/default-material-textures', {
      ...body(docId, submodel, hardware, images.map((one) => (one ? '1' : null))), model, preview: true,
    },
  );
  let n = 0;
  const urls = hardware.flatMap((h, i) => {
    const url = images[i];
    return url ? [{ url, caption: `${++n} · ${h.label}` }] : [];
  });
  return { ...data, urls };
}

// ASK. Answers the reading's hardware half; throws what the server said on a failure.
export async function askHardware(
  docId: string, submodel: string, model: string, file: Blob, hardware: HardwarePart[],
): Promise<HardwareReading> {
  store.patch(docId, { busy: true });
  const asked = getStandardMaterials(docId);
  try {
    const images = await drawings(docId, file, hardware);
    const { data } = await axios.post<{
      textures: (MaterialTexture | null)[]; surfaces?: (StandardSurface | null)[];
      colours?: (Rgb | null)[]; parts?: [number, number, string?][]; reasoning: string; raw: string;
    }>(
      '/api/notice/default-material-textures', { ...body(docId, submodel, hardware, images), model },
    );
    return {
      // Every material asked about, as it stood, with what the answer said of it.
      materials: asked.map((m, i) => ({
        pair: m.pair,
        rgb: m.rgb,
        ...(data.colours?.[i] ? { colour: data.colours[i] } : {}),
        ...(data.textures[i] ? { texture: data.textures[i] } : {}),
        ...(data.surfaces?.[i] || {}),
      })),
      asked: hardware.map((h) => h.at),
      // [part number, material number, which part of it], both numbers from 1 into what was
      // sent — a tool made of two things, twice, each saying which part of the tool it is.
      picks: (data.parts || []).flatMap(([n, i, of]) => {
        const part = hardware[n - 1];
        const pair = asked[i - 1]?.pair;
        return part && pair ? [{ at: part.at, pair, ...(of ? { of } : {}) }] : [];
      }),
      reasoning: data.reasoning || '',
      raw: data.raw || '',
    };
  } finally {
    store.patch(docId, { busy: false });
  }
}

// A READING'S HARDWARE HALF ONTO THE MANUAL'S DEFAULT MATERIALS, by their words: each keeps its
// name (edited by hand, perhaps, since) and takes the colour, surface and texture the reading
// said. A material the reading did not ask about keeps what it had. The parts are the caller's
// (see useMaterialParts' assign, which repaints a part's kept mesh).
export function applyHardware(docId: string, hardware: HardwareReading): void {
  const same = (a: { material: string; color: string }, b: { material: string; color: string }) =>
    a.material === b.material && a.color === b.color;
  setStandardMaterials(docId, getStandardMaterials(docId).map((m) => {
    const said = hardware.materials.find((one) => same(one.pair, m.pair));
    if (!said) return m;
    const { colour, texture, roughness, metallic, fibre } = said;
    return {
      ...m,
      ...(colour ? { rgb: colour } : {}),
      ...(texture ? { texture } : {}),
      ...(roughness != null ? { roughness } : {}),
      ...(metallic != null ? { metallic } : {}),
      ...(fibre != null ? { fibre } : {}),
    };
  }));
}
